import { useQuery } from "@tanstack/react-query";
import cx from "classnames";
import { useEffect, useState } from "react";
import { LoaderFunction, useLoaderData, useNavigate } from "react-router-dom";

import { ReferralRewardsAcknowledgeRewardsModal } from "@/modules/referralRewards";
import { AwardedReferralRewardsList } from "@/modules/referralRewards/AwardedReferralRewardsList/AwardedReferralRewardsList";
import { getAvailableReferralRewardForPlatformLocation } from "@/modules/referralRewards/referralRewards.utils";
import { ReferralRewardsCard } from "@/modules/referralRewards/ReferralRewardsCard/ReferralRewardsCard";
import { RewardProgressV2 } from "@/modules/rewards/RewardProgressV2/RewardProgressV2";
import TopNavV3 from "@/molecules/navbars/TopNavV3";
import { get, pget } from "api";
import { RewardStatus } from "api/types";
import { AnimatedBackground } from "components/AnimatedBackground";
import { RedeemedRewardProps } from "molecules/containers/reward/redeemed-reward/RedeemedReward";
import { RewardProgress } from "molecules/containers/reward-progress/RewardProgress";
import TopNav from "molecules/navbars/TopNav";
import { UserAndCash } from "molecules/subheaders/user-and-cash/UserAndCash";
import { AvailableRewardList } from "organisms/available-reward-list/AvailableRewardList";
import { RedeemedRewardList } from "organisms/redeemed-reward-list/RedeemedRewardList";
import { ReferAFriend } from "organisms/refer-a-friend/ReferAFriend";
import { UpcomingRewardList } from "organisms/upcoming-reward-list/UpcomingRewardList";
import { queryClient } from "pages/Root";
import { useFeatureFlag } from "services/Flags";
import { waitObj } from "toolbox/Promise";

const getAwardedReferralRewards = pget(
  "/incentives/{platformId}/referral-rewards/{personId}/redeemable"
);

const load = async () => {
  const platform = await queryClient.fetchQuery(["/platform"], () =>
    get("/platform")
  );

  const visits = queryClient
    .fetchQuery(["/visit/count"], () => get("/visit/count"))
    .then((v) => v.totalVisits);

  const userInfo = await queryClient.fetchQuery(["/user"], () => get("/user"), {
    staleTime: 1000 * 60 * 2,
  });

  const rewards = queryClient.fetchQuery(["/rewards"], () =>
    get("/rewards", {})
  );

  const awardedReferralRewards = queryClient
    .ensureQueryData({
      queryKey: getAwardedReferralRewards.key({
        platformId: platform?.id ?? "",
        personId: userInfo.id,
      }),
      queryFn: () =>
        getAwardedReferralRewards({
          platformId: platform.id,
          personId: userInfo.id,
        }),
    })
    .catch(() => null);

  const referralRewardForPlatformLocation = queryClient
    .fetchQuery(
      [
        "/incentives/{platformLocationId}/available-referral-reward",
        platform.id,
      ],
      () =>
        get("/incentives/{platformLocationId}/available-referral-reward", {
          platformLocationId: userInfo?.platformLocation?.id ?? "",
        })
    )
    .catch(() => null);

  const activeSignupReward = queryClient
    .fetchQuery(["/incentives/{platformLocationId}/active-signup-reward"], () =>
      get("/incentives/{platformLocationId}/active-signup-reward", {
        platformLocationId: userInfo?.platformLocation?.id ?? "",
      })
    )
    .catch(() => null);

  return waitObj({
    visits,
    userInfo,
    rewards,
    platform,
    activeSignupReward,
    referralReward: referralRewardForPlatformLocation,
    awardedReferralRewards,
  });
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const rewardsIndexLoader: LoaderFunction = async () => {
  const data = await load();

  // Redirect to home if rewards are disabled
  if (!data.platform?.platformSettings?.rewardsEnabled) {
    return new Response("", {
      status: 302,
      headers: {
        Location: "/home",
      },
    });
  }

  return data;
};

const RewardsIndex = () => {
  const { rewards, userInfo, platform, awardedReferralRewards } =
    useLoaderData() as LoaderData;
  const navigate = useNavigate();
  const isReferralProgramEnabled = useFeatureFlag("referral-rewards-phase-2");
  const isV3Enabled = true;
  const [showCountUpAnimation, setShowCountUpAnimation] = useState(false);

  useEffect(() => {
    if (showCountUpAnimation) {
      const timer = setTimeout(() => {
        setShowCountUpAnimation(false);
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [showCountUpAnimation]);

  const handleReferralRewardAcknowledged = () => {
    setShowCountUpAnimation(true);
  };

  const handleRewardNavigation = (
    rewardId: string,
    referralRewardId: string | null | undefined,
    platformUserRewardId: string | null | undefined = null
  ) => {
    const searchParams = new URLSearchParams();
    if (referralRewardId) {
      searchParams.set("referralRewardId", referralRewardId);
    }

    if (platformUserRewardId) {
      searchParams.set("platformUserRewardId", platformUserRewardId);
    }

    const url = `/rewards/${rewardId}${
      searchParams.toString() ? `?${searchParams.toString()}` : ""
    }`;
    navigate(url);
  };

  const available = rewards
    .filter((r) => !userInfo.isGuest && r.status === RewardStatus.Available)
    .filter((r) => !r.isInOfficeReferralReward)
    .map((r) => ({
      id: r.rewardId,
      referralRewardId: r.referralRewardId,
      title: r.title,
      valueIfBanked: r.valueIfBanked,
      onClick: () =>
        handleRewardNavigation(
          r.rewardId,
          r.referralRewardId,
          r.platformUserRewardId ?? null
        ),
      repeatCashEnabled: r.repeatCashEnabled,
      disclaimer: r.termsAndConditions,
    }));

  const future = rewards
    .filter((r) => r.status === RewardStatus.NotAvailable)
    .filter((r) => !r.isInOfficeReferralReward)
    .map((r) => ({
      id: r.rewardId,
      title: r.title,
      visitsToUnlock: r.minimumVisits,
      valueIfBanked: r.valueIfBanked,
      repeatCashEnabled: r.repeatCashEnabled,
      disclaimer: r.termsAndConditions,
      minimumVisits: r.minimumVisits,
      cashToUnlock: r.cashToUnlock,
      unlockProgressPercent: r.unlockProgressPercent,
    }));

  const redeemed = rewards
    .filter(
      (r) =>
        r.status === RewardStatus.Redeemed || r.status === RewardStatus.CashedIn
    )
    .filter((r) => !r.isInOfficeReferralReward)
    .map((r) => ({
      id: r.rewardId,
      referralRewardId: r.referralRewardId,
      title: r.title,
      earnedRewardStatus:
        r.status === RewardStatus.Redeemed
          ? RewardStatus.Redeemed
          : RewardStatus.CashedIn,
      redeemedDateTime: r.redeemedDateTime,
      onClick: () =>
        handleRewardNavigation(
          r.rewardId,
          r.referralRewardId,
          r.platformUserRewardId
        ),
      disclaimer: r.termsAndConditions,
    })) as RedeemedRewardProps[];

  const nextReward = future.length
    ? future.reduce(
        (acc, curr) =>
          acc.minimumVisits ?? 0 <= (curr.minimumVisits ?? 0) ? acc : curr,
        future[0]
      )
    : undefined;

  const renderProgress = () => {
    if (!isReferralProgramEnabled) {
      const progressContent = (
        <>
          <UserAndCash
            firstName={userInfo.firstName}
            lastName={userInfo.lastName}
            repeatCashBalance={userInfo.repeatCashBalance}
            hideRepeatCash={!platform.platformSettings?.repeatCashEnabled}
          />
          <RewardProgress
            nextMilestone={nextReward?.minimumVisits ?? 0}
            rewardsCount={available.length}
            cashToUnlock={nextReward?.cashToUnlock}
            unlockProgressPercent={
              future.length == 0 ? 100 : nextReward?.unlockProgressPercent ?? 0
            }
            marketplaceEnabled={platform.platformSettings?.marketplaceEnabled}
            visitCashValue={platform.platformSettings?.visitCashValue}
            spendBasedRewardsEnabled={
              platform?.platformSettings?.spendBasedRewardsEnabled ?? true
            }
          />
        </>
      );

      return isV3Enabled ? (
        <AnimatedBackground mode="light">{progressContent}</AnimatedBackground>
      ) : (
        progressContent
      );
    }
  };

  const renderTopNav = () => {
    if (isV3Enabled) {
      return (
        <AnimatedBackground mode="light-prominent">
          <TopNavV3 hideShadow={true} backgroundVariant="transparent">
            <h1 className="text-xl">Rewards</h1>
          </TopNavV3>

          <RewardProgressV2
            displayGreetings={true}
            nextMilestone={nextReward?.minimumVisits || 0}
            rewardsCount={available?.length || 0}
            cashToUnlock={nextReward?.cashToUnlock}
            unlockProgressPercent={
              future.length == 0 ? 100 : nextReward?.unlockProgressPercent || 0
            }
            marketplaceEnabled={platform.platformSettings?.marketplaceEnabled}
            visitCashValue={platform.platformSettings?.visitCashValue}
            spendBasedRewardsEnabled={
              platform?.platformSettings?.spendBasedRewardsEnabled ?? true
            }
            showCountUpAnimation={showCountUpAnimation}
          />
        </AnimatedBackground>
      );
    }
    return (
      <TopNav>
        <h1 className="text-xl">Rewards</h1>
      </TopNav>
    );
  };

  return (
    <div>
      {renderTopNav()}

      {renderProgress()}

      <ReferralRewardsCard />

      <div
        className={cx(
          !isReferralProgramEnabled && "px-4 py-8",
          "flex flex-col  bg-[#FAFAFA] py-5 empty:hidden"
        )}
      >
        <AvailableRewardList
          {...(isReferralProgramEnabled && {
            titleClassName: "text-start pl-4 py-4 text-sub2 text-black",
            isReferralProgramEnabled: true,
          })}
          rewards={available}
        />
        <UpcomingRewardList
          {...(isReferralProgramEnabled && {
            titleClassName: "text-start pl-4 py-4 text-sub2 text-black",
            isReferralProgramEnabled: true,
          })}
          rewards={future}
        />
        <RedeemedRewardList
          {...(isReferralProgramEnabled && {
            titleClassName: "text-start pl-4 py-4 text-sub2 text-black",
            isReferralProgramEnabled: true,
          })}
          rewards={redeemed}
        />
        <AwardedReferralRewardsList
          awardedReferralRewards={
            awardedReferralRewards?.redeemableAwardedReferralRewards
          }
        />
      </div>

      <ReferAFriend withRepeatMDFooter />

      <ReferralRewardsAcknowledgeRewardsModal
        onCloseAfterInteracting={handleReferralRewardAcknowledged}
        platformId={platform?.id ?? ""}
      />
    </div>
  );
};

export default RewardsIndex;
